import { useMemo } from 'react';

import { isNavigableCategory } from '../utils/utils';
import { LobbyCasinoGamesCategory, useCasinoLobbyGroupsQuery } from './use-casino-games';
import { useNonNavigableCategories } from './use-non-navigable-categories';

export type SideRailCategory = {
    id: string;
    name: string;
    count: number;
    isBetrLiveDealer: boolean;
};

const isBetrLiveDealerCategory = (category: LobbyCasinoGamesCategory) =>
    category.name.trim().toLowerCase() === 'betr live dealer';

/**
 * Interim promotion to the top section while `Lobby-web` carries no `isTopCategory` flags.
 * Remove once the back office sets the flag on these categories per the spec.
 */
const INTERIM_SECTION_CATEGORIES = ['new games', 'betr live dealer'];

const isSectionCategory = (category: LobbyCasinoGamesCategory) =>
    category.isTopCategory || INTERIM_SECTION_CATEGORIES.includes(category.name.trim().toLowerCase());

/**
 * Categories of the desktop casino side rail, split into the top section rows.
 * Reads the lobby datasource — resolved to `Lobby-web` on web by the query hook.
 */
export const useCasinoSideRailCategories = () => {
    const groupsQuery = useCasinoLobbyGroupsQuery('Lobby');
    const nonNavigableCategories = useNonNavigableCategories();

    const { sectionCategories, allGamesCategories } = useMemo(() => {
        const seen = new Set<string>();
        const categories: Array<LobbyCasinoGamesCategory> = [];
        groupsQuery.data?.pages.forEach(page => {
            page.items.forEach(category => {
                if (!seen.has(category.id) && isNavigableCategory(category.name, nonNavigableCategories)) {
                    seen.add(category.id);
                    categories.push(category);
                }
            });
        });

        const toRailCategory = (category: LobbyCasinoGamesCategory): SideRailCategory => ({
            id: category.id,
            name: category.name,
            count: category.games.total,
            isBetrLiveDealer: isBetrLiveDealerCategory(category),
        });

        return {
            sectionCategories: categories.filter(isSectionCategory).map(toRailCategory),
            allGamesCategories: categories.filter(category => !isSectionCategory(category)).map(toRailCategory),
        };
    }, [groupsQuery.data?.pages, nonNavigableCategories]);

    return { sectionCategories, allGamesCategories, isLoading: groupsQuery.isLoading };
};
